import { Injectable, OnDestroy, effect, DestroyRef, inject, EffectRef } from '@angular/core';
import { Subject, Observable, Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TtsStreamService } from './tts-stream.service';
import { CallStoreService } from 'src/app/pages/learner/call/library/call-store/call-store.service';
import { AssemblyService } from 'src/app/pages/learner/call/library/assembly/assembly.service';
import { CallService } from './call.service';
import { LoggerService } from '../logger/logger.service';
import { AppConstants } from 'src/app/app-constants';

export enum ConversationState {
  IDLE = 'idle',
  INITIALIZING = 'initializing',
  AGENT_SPEAKING = 'agent_speaking',
  LISTENING = 'listening',
  USER_SPEAKING = 'user_speaking',
  PROCESSING = 'processing',
  ENDED = 'ended',
  ERROR = 'error',
}

export interface IChatMessage {
  role: 'agent' | 'user';
  content: string;
  timestamp: Date;
}

export interface ISegmentProvider {
  getUserSpokenResponse(): string;
  getCallLearnUuid(): string;
}

/**
 * AudioCallControlService - Orchestrates audio calls
 *
 * This service coordinates between:
 * - AssemblyService (Speech-to-Text)
 * - TtsStreamService (Text-to-Speech from backend stream)
 * - CallStoreService (State management)
 *
 * It manages:
 * - Conversation state (who's speaking)
 * - Turn-taking logic (pause STT when agent speaks)
 * - Silence detection (configurable timeout)
 * - Conversation history
 */
@Injectable({
  providedIn: 'root',
})
export class AudioCallControlService implements OnDestroy {
  /**
   * Silence timeout: 3000ms accommodates natural mid-sentence pauses without
   * prematurely triggering PROCESSING while the user is still speaking.
   * Too short (<500ms) cuts users off; too long (>3000ms) feels unresponsive.
   */
  private static readonly DEFAULT_SILENCE_TIMEOUT_MS = 3000;
  private static readonly HARDCODED_CALL_UUID = '69737c06-86ec-40d5-acc9-3565cfd29045';
  private readonly silenceTimeoutMs: number = AudioCallControlService.DEFAULT_SILENCE_TIMEOUT_MS;
  private readonly hardcodedCallUuid: string = AudioCallControlService.HARDCODED_CALL_UUID;

  private _state: ConversationState = ConversationState.IDLE;
  private _conversationHistory: IChatMessage[] = [];
  private _currentTranscript: string = '';
  private _partialTranscript: string = '';
  private _currentAgentSpeech: string = '';
  private _pendingWelcomeMessage: string = '';
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;
  private lastTranscriptTime: number = 0;
  private lastProcessedTranscript: string = '';
  private isWatchingTranscripts: boolean = false;
  private _skipListenCycleOnNextSpeakEnd = false;
  private subscriptions: Subscription[] = [];
  private destroyRef = inject(DestroyRef);
  private effectRefs: EffectRef[] = [];
  private segmentProvider: ISegmentProvider | null = null;
  private readonly _stateChange$ = new Subject<ConversationState>();
  private readonly _agentMessage$ = new Subject<string>();
  private readonly _userMessage$ = new Subject<string>();
  private readonly _transcriptUpdate$ = new Subject<string>();
  private readonly _captionText$ = new Subject<string>();
  private readonly _error$ = new Subject<Error>();
  private readonly _callEnded$ = new Subject<void>();

  public get onStateChange$(): Observable<ConversationState> {
    return this._stateChange$.asObservable();
  }

  public get onAgentMessage$(): Observable<string> {
    return this._agentMessage$.asObservable();
  }

  public get onUserMessage$(): Observable<string> {
    return this._userMessage$.asObservable();
  }

  public get onTranscriptUpdate$(): Observable<string> {
    return this._transcriptUpdate$.asObservable();
  }

  /** Emits originalAgentResponse for caption display when agent replies (processUserResponse). */
  public get onCaptionText$(): Observable<string> {
    return this._captionText$.asObservable();
  }

  public get onError$(): Observable<Error> {
    return this._error$.asObservable();
  }

  public get onCallEnded$(): Observable<void> {
    return this._callEnded$.asObservable();
  }

  get state(): ConversationState {
    return this._state;
  }

  get conversationHistory(): IChatMessage[] {
    return [...this._conversationHistory];
  }

  get currentTranscript(): string {
    return this._currentTranscript;
  }

  get partialTranscript(): string {
    return this._partialTranscript;
  }

  get currentAgentSpeech(): string {
    return this._currentAgentSpeech;
  }

  get isAgentSpeaking(): boolean {
    return this._state === ConversationState.AGENT_SPEAKING;
  }

  get isListening(): boolean {
    return this._state === ConversationState.LISTENING || 
           this._state === ConversationState.USER_SPEAKING;
  }

  constructor(
    private readonly ttsStreamService: TtsStreamService,
    private readonly callStore: CallStoreService,
    private readonly assemblyService: AssemblyService,
    private readonly callService: CallService,
    private readonly logger: LoggerService
  ) {
    this.logger.setFileContext({ service: 'AudioCallControlService' });
    this.initialize();
  }

  private initialize(): void {
    this.ttsStreamService.onSpeakingStart$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.handleAgentStartSpeaking();
      });

    this.ttsStreamService.onSpeakingEnd$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.handleAgentStopSpeaking();
      });

    this.ttsStreamService.onError$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((error) => {
        this.handleError(error);
      });

    this.setupTranscriptWatcher();

    this.logger.log('[AUDIO_CALL_CONTROL] Initialized', { 
      silenceTimeoutMs: this.silenceTimeoutMs 
    });
  }

  private setupTranscriptWatcher(): void {
    const finalTranscriptEffect = effect(() => {
      const transcript = this.callStore.getSpeechTranscript();

      if (this.isWatchingTranscripts && transcript && transcript !== this.lastProcessedTranscript) {
        this.logger.log('[AUDIO_CALL_CONTROL] Final transcript detected', { transcript });
        this.lastProcessedTranscript = transcript;
        this.handleTranscriptUpdate(transcript, true);
      }
    });

    const partialTranscriptEffect = effect(() => {
      const partialTranscript = this.callStore.getSpeechTranscriptPartial();
      const partialExists = this.callStore.getPartialTranscriptExists();

      if (this.isWatchingTranscripts && partialExists && partialTranscript) {
        this.logger.log('[AUDIO_CALL_CONTROL] Partial transcript', { partialTranscript });
        this.handleTranscriptUpdate(partialTranscript, false);
      }
    });

    const userSpokeEffect = effect(() => {
      const userSpoke = this.callStore.getUserSpoke();

      if (this.isWatchingTranscripts && userSpoke > 0) {
        if (this._state === ConversationState.LISTENING) {
          this.setState(ConversationState.USER_SPEAKING);
        }
      }
    });

    this.effectRefs.push(finalTranscriptEffect, partialTranscriptEffect, userSpokeEffect);
    this.destroyRef.onDestroy(() => {
      this.cleanupEffects();
    });

    this.logger.log('[AUDIO_CALL_CONTROL] Transcript watcher set up');
  }

  private cleanupEffects(): void {
    this.effectRefs.forEach(effectRef => {
      if (effectRef && typeof effectRef.destroy === 'function') {
        effectRef.destroy();
      }
    });
    this.effectRefs = [];
  }

  private startWatchingTranscripts(): void {
    this.isWatchingTranscripts = true;
    this.lastProcessedTranscript = '';
    this.logger.log('[AUDIO_CALL_CONTROL] Started watching transcripts');
  }

  private stopWatchingTranscripts(): void {
    this.isWatchingTranscripts = false;
    this.logger.log('[AUDIO_CALL_CONTROL] Stopped watching transcripts');
  }

  /**
   * Start the audio call
   * @param greetingMessage Optional greeting message for the agent to speak
   */
  async startCall(greetingMessage?: string): Promise<void> {
    this.logger.log('[AUDIO_CALL_CONTROL] Starting call');

    try {
      this.setState(ConversationState.INITIALIZING);
      this._conversationHistory = [];
      this.clearTranscriptState();
      this.clearSilenceTimer();
      this.callStore.setIsVisualAidOpen(false);

      await this.assemblyService.init();
      await this.assemblyService.startTranscriber(true);

      const greeting =
        greetingMessage?.trim() ||
        this._pendingWelcomeMessage.trim() ||
        this.getGreeting();
      if (this._pendingWelcomeMessage) {
        this._pendingWelcomeMessage = '';
      }
      this.agentSpeak(greeting);

      this.logger.log('[AUDIO_CALL_CONTROL] Call started successfully');
    } catch (error) {
      this.handleError(error as Error, 'Failed to start call');
      throw error;
    }
  }

  async endCall(): Promise<void> {
    this.logger.log('[AUDIO_CALL_CONTROL] Ending call');

    this.stopWatchingTranscripts();
    this.clearSilenceTimer();
    this.ttsStreamService.stop();
    await this.assemblyService.stop();

    this.setState(ConversationState.ENDED);
    this._callEnded$.next();
  }

  /**
   * Make the agent speak a message (e.g. greeting or reply).
   * Uses the streaming service to request and play agent audio from the backend.
   */
  agentSpeak(text: string): void {
    if (!text.trim()) {
      this.logger.warn('[AUDIO_CALL_CONTROL] Empty text provided to agentSpeak');
      return;
    }

    this.logger.log('[AUDIO_CALL_CONTROL] Agent speaking', {
      textPreview: text.substring(0, 50) + '...',
    });

    this.pauseListening();
    this.addMessage('agent', text);
    this._agentMessage$.next(text);
    this._currentAgentSpeech = text;

    this.playAgentAudioStream(text);
  }

  registerSegmentProvider(provider: ISegmentProvider): void {
    this.segmentProvider = provider;
    this.logger.log('[AUDIO_CALL_CONTROL] Segment provider registered');
  }

  unregisterSegmentProvider(): void {
    this.segmentProvider = null;
    this.logger.log('[AUDIO_CALL_CONTROL] Segment provider unregistered');
  }

  /**
   * Process user's utterance (called when silence is detected).
   * Calls processUserResponse to get agent reply text, then plays it via audioStream.
   * If a segment provider is registered, uses formatted segments; otherwise uses plain transcript.
   */
  processUserUtterance(transcript: string): void {
    if (!transcript.trim()) {
      this.logger.warn('[AUDIO_CALL_CONTROL] Empty transcript, skipping');
      this.startSilenceTimer();
      return;
    }

    const userSpokenResponse = this.segmentProvider
      ? this.segmentProvider.getUserSpokenResponse()
      : transcript;
    const callLearnUuid = this.segmentProvider
      ? this.segmentProvider.getCallLearnUuid()
      : undefined;

    let effectiveUserSpokenResponse = userSpokenResponse.trim();

    // If using segment provider but no segments available, fallback to transcript
    if (this.segmentProvider && !userSpokenResponse.trim()) {
      this.logger.warn(
        '[AUDIO_CALL_CONTROL] Segment provider returned empty response, falling back to transcript'
      );
      effectiveUserSpokenResponse = transcript.trim();
    }

    const practiceCallUuid =
      this.callStore.getPracticeCallUuid()?.trim() || this.hardcodedCallUuid;
    const callUuid = this.callStore.getCallUuid()?.trim() ?? '';

    if (!callUuid) {
      this.logger.error(
        '[AUDIO_CALL_CONTROL] Missing callUuid for processUserResponse',
        new Error('callUuid is required')
      );
      this.handleError(
        new Error('callUuid is required for processUserResponse'),
        'Missing callUuid'
      );
      this.setState(ConversationState.LISTENING);
      this.startWatchingTranscripts();
      this.startSilenceTimer();
      return;
    }

    this.logger.log('[AUDIO_CALL_CONTROL] Processing user utterance', {
      transcript: transcript.substring(0, 50) + (transcript.length > 50 ? '...' : ''),
      hasSegmentProvider: !!this.segmentProvider,
      userSpokenResponse:
        effectiveUserSpokenResponse.substring(0, 50) +
        (effectiveUserSpokenResponse.length > 50 ? '...' : ''),
    });

    this.stopWatchingTranscripts();
    this.setState(ConversationState.PROCESSING);
    this.addMessage('user', transcript);
    this._userMessage$.next(transcript);
    this._currentTranscript = '';
    this._partialTranscript = '';

    this.callService
      .processUserResponse(
        practiceCallUuid,
        callUuid,
        effectiveUserSpokenResponse,
        callLearnUuid
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const agentText =
            res.pronunciationCorrectedResponse?.trim() ||
            res.originalAgentResponse?.trim() ||
            '';
          if (!agentText) {
            this.logger.warn(
              '[AUDIO_CALL_CONTROL] processUserResponse returned no agent text'
            );
            this.setState(ConversationState.LISTENING);
            this.startWatchingTranscripts();
            this.startSilenceTimer();
            return;
          }
          this._captionText$.next(res.originalAgentResponse?.trim() ?? '');
          this.addMessage('agent', agentText);
          this._agentMessage$.next(agentText);
          this._currentAgentSpeech = agentText;
          this.playAgentAudioStream(agentText);
        },
        error: (err) => {
          this.handleError(
            err instanceof Error ? err : new Error(String(err)),
            'processUserResponse failed'
          );
          this.setState(ConversationState.LISTENING);
          this.startWatchingTranscripts();
          this.startSilenceTimer();
        },
      });
  }

  /**
   * Requests agent audio stream from the backend and plays it via TtsStreamService.
   * Used for both greeting/reply (agentSpeak) and after user utterance (processUserUtterance).
   */
  private playAgentAudioStream(text: string): void {
    const practiceCallUuid =
      this.callStore.getPracticeCallUuid()?.trim() || this.hardcodedCallUuid;
    const stream$ = this.callService.getAudioStream(practiceCallUuid, text);
    this.ttsStreamService.playStream(stream$);
  }

  /**
   * Handle incoming transcript from Assembly AI
   * Call this method when transcript updates are received
   * @param transcript The partial or final transcript
   * @param isFinal Whether this is a final transcript
   */
  handleTranscriptUpdate(transcript: string, isFinal: boolean): void {
    if (this._state === ConversationState.AGENT_SPEAKING || 
        this._state === ConversationState.PROCESSING) {
      return;
    }

    this.resetSilenceTimer();

    if (isFinal) {
      this._currentTranscript = this._currentTranscript 
        ? `${this._currentTranscript} ${transcript}` 
        : transcript;
      this._partialTranscript = '';
      this.logger.log('[AUDIO_CALL_CONTROL] Final transcript', { 
        transcript: this._currentTranscript 
      });
    } else {
      this._partialTranscript = transcript;
      this.setState(ConversationState.USER_SPEAKING);
    }

    this._transcriptUpdate$.next(this._currentTranscript || this._partialTranscript);
  }

  forceProcessTranscript(): void {
    this.clearSilenceTimer();

    const transcript = this._currentTranscript || this._partialTranscript;
    if (transcript.trim()) {
      this.processUserUtterance(transcript.trim());
    }
  }

  async setMicMuted(muted: boolean): Promise<void> {
    this.logger.log('[AUDIO_CALL_CONTROL] Mic muted', { muted });

    if (muted) {
      await this.pauseListening();
      this.clearSilenceTimer();
    } else {
      await this.resumeListening();
      if (this._state === ConversationState.LISTENING || 
          this._state === ConversationState.USER_SPEAKING) {
        this.startSilenceTimer();
      }
    }

    this.callStore.setIsMicOn(!muted);
    this.callStore.setUserMicPreference(!muted);
  }

  private clearTranscriptState(): void {
    this._currentTranscript = '';
    this._partialTranscript = '';
    this.lastProcessedTranscript = '';
    this.callStore.setSpeechTranscript('');
    this.callStore.setSpeechTranscriptPartial('');
    this.callStore.setRecognizedTranscripts([]);
    this.callStore.setPartialTranscriptExists(false);
  }

  private setState(state: ConversationState): void {
    if (this._state !== state) {
      this.logger.log('[AUDIO_CALL_CONTROL] State change', { 
        from: this._state, 
        to: state 
      });
      this._state = state;
      this._stateChange$.next(state);
    }
  }

  private addMessage(role: 'agent' | 'user', content: string): void {
    this._conversationHistory.push({
      role,
      content,
      timestamp: new Date(),
    });
  }

  private handleAgentStartSpeaking(): void {
    this.logger.log('[AUDIO_CALL_CONTROL] Agent started speaking');
    this.stopWatchingTranscripts();
    this.pauseListening();
    this.setState(ConversationState.AGENT_SPEAKING);
    this.clearSilenceTimer();
  }

  private async handleAgentStopSpeaking(): Promise<void> {
    if (this._skipListenCycleOnNextSpeakEnd) {
      this._skipListenCycleOnNextSpeakEnd = false;
      this.logger.log('[AUDIO_CALL_CONTROL] Skipping listen cycle');
      return;
    }
    this.logger.log('[AUDIO_CALL_CONTROL] Agent stopped speaking');
    this._currentAgentSpeech = '';
    this.clearTranscriptState();
    this.startWatchingTranscripts();
    this.setState(ConversationState.LISTENING);
    this.startSilenceTimer();
    if (this.callStore.getUserMicPreference()) {
      await this.resumeListening();
    } else {
      this.logger.log('[AUDIO_CALL_CONTROL] Mic muted by user preference, skipping listen cycle');
      this.setState(ConversationState.LISTENING);
    }
  }

  private async pauseListening(): Promise<void> {
    this.logger.log('[AUDIO_CALL_CONTROL] Pausing STT');
    await this.assemblyService.pause();
  }

  private async resumeListening(): Promise<void> {
    this.logger.log('[AUDIO_CALL_CONTROL] Resuming STT');
    await this.assemblyService.resume();
  }

  private startSilenceTimer(): void {
    this.clearSilenceTimer();
    this.lastTranscriptTime = Date.now();

    this.silenceTimer = setTimeout(() => {
      this.handleSilenceTimeout();
    }, this.silenceTimeoutMs);

    this.logger.log('[AUDIO_CALL_CONTROL] Silence timer started', { 
      timeoutMs: this.silenceTimeoutMs 
    });
  }

  private resetSilenceTimer(): void {
    if (this.silenceTimer) {
      this.clearSilenceTimer();
      this.startSilenceTimer();
    }
  }

  private clearSilenceTimer(): void {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
  }

  private handleSilenceTimeout(): void {
    this.logger.log('[AUDIO_CALL_CONTROL] Silence timeout reached');

    const transcript = this._currentTranscript || this._partialTranscript;

    if (transcript.trim()) {
      this.processUserUtterance(transcript.trim());
    } else {
      this.logger.log('[AUDIO_CALL_CONTROL] No speech detected, restarting timer');
      this.startSilenceTimer();
    }
  }

  /**
   * Standardized error handling: logs, emits via onError$, and sets ERROR state.
   */
  private handleError(error: Error, context?: string): void {
    const errorMessage = context || 'Error occurred';
    this.logger.error(`[AUDIO_CALL_CONTROL] ${errorMessage}`, error, { 
      errorMessage: error.message,
      context,
      state: this._state,
      callUuid: this.callStore.getCallUuid()?.trim() ?? '',
      practiceCallUuid: this.callStore.getPracticeCallUuid()?.trim() ?? '',
    });
    
    this._error$.next(error);
    this.setState(ConversationState.ERROR);
  }

  getGreeting(): string {
    return "Hello! I'm your AI assistant. How can I help you today?";
  }

  /**
   * Set the welcome message to use for the next startCall().
   * Used by pathway when initiatePathwayStepSimulation returns.
   * Pass empty string to clear.
   */
  setWelcomeMessage(text: string): void {
    this._pendingWelcomeMessage = text ?? '';
  }

  /**
   * Call this before playing TTS that bypasses the normal agent-speak flow
   * (e.g. quiz result outcome audio). The next `onSpeakingEnd$` emission will be
   * ignored so the service does not resume the STT listener or start a silence timer.
   */
  skipListenCycleOnNextSpeakEnd(): void {
    this._skipListenCycleOnNextSpeakEnd = true;
  }

  ngOnDestroy(): void {
    this.clearSilenceTimer();
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.cleanupEffects();
    this._stateChange$.complete();
    this._agentMessage$.complete();
    this._userMessage$.complete();
    this._transcriptUpdate$.complete();
    this._captionText$.complete();
    this._error$.complete();
    this._callEnded$.complete();
    this.logger.clearFileContext();
  }
}